/**
 * CREATIVE DETONATOR - Layout Utilities
 *
 * Shared utilities for header/footer injection, including path detection
 * and page identification for active nav states.
 */

/**
 * Detects the current page's depth and returns appropriate path prefix
 * @returns {string} Path prefix for assets ('' | '../' | '../../')
 */
export function getPathPrefix() {
  const path = window.location.pathname;

  // Root level (e.g., /index.html or /)
  if (path === '/' || path.match(/^\/[^\/]*\.html$/) || path.endsWith('/frontend/') || path.match(/^\/frontend\/[^\/]*\.html$/)) {
    return '';
  }

  // Handle local development with /frontend/ prefix
  const normalizedPath = path.replace(/^\/frontend/, '');

  // Root level after normalization
  if (normalizedPath === '/' || normalizedPath.match(/^\/[^\/]*\.html$/)) {
    return '';
  }

  // One level deep (e.g., /pages/about.html)
  if (normalizedPath.match(/^\/pages\/[^\/]*\.html$/)) {
    return '../';
  }

  // Two levels deep (e.g., /features/grid-splitter/index.html)
  if (normalizedPath.match(/^\/pages\/[^\/]+\/[^\/]*\.html$/) ||
      normalizedPath.match(/^\/features\/[^\/]+\/[^\/]*\.html$/)) {
    return '../../';
  }

  // Three levels deep (e.g., /features/method/pages/chamber.html)
  if (normalizedPath.match(/^\/features\/[^\/]+\/[^\/]+\/[^\/]*\.html$/)) {
    return '../../../';
  }

  // Default to two levels for unknown paths
  return '../../';
}

/**
 * Detects current page identifier for active nav state
 * @returns {string} Page identifier (method, triggers, about, generator, dashboard, index)
 */
export function getCurrentPage() {
  const path = window.location.pathname;
  const normalizedPath = path.replace(/^\/frontend/, '');

  // Method pages
  if (normalizedPath.includes('/method/')) return 'method';

  // Triggers pages
  if (normalizedPath.includes('/triggers/')) return 'triggers';

  // Dashboard pages
  if (normalizedPath.includes('/dashboard/')) return 'dashboard';

  // Single pages
  if (normalizedPath.includes('/about-method')) return 'resources';
  if (normalizedPath.includes('/about')) return 'about';
  if (normalizedPath.includes('/generator')) return 'generator';
  if (normalizedPath.includes('/resources')) return 'resources';

  // Root/index
  const filename = normalizedPath.split('/').pop()?.replace('.html', '') || 'index';
  return filename;
}

/**
 * Check if current page is a dashboard page (uses different layout)
 * @returns {boolean}
 */
export function isDashboardPage() {
  const path = window.location.pathname;
  return path.includes('/dashboard/');
}

/**
 * Check if current page is an auth page (uses minimal layout)
 * @returns {boolean}
 */
export function isAuthPage() {
  const path = window.location.pathname;
  return path.includes('/auth/');
}
